import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageSquare, Send, Check, X, AtSign, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  new: { label: 'Ny', color: 'bg-slate-600 text-slate-200' },
  accepted: { label: 'Tatt til følge', color: 'bg-emerald-600/20 text-emerald-400' },
  dismissed: { label: 'Ikke aktuelt', color: 'bg-slate-700/50 text-slate-400' },
};

function MentionInput({ value, onChange, onSubmit, users, placeholder }) {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filter, setFilter] = useState('');
  const inputRef = useRef(null);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSubmit();
    }
  };

  const handleChange = (e) => {
    const val = e.target.value;
    onChange(val);
    // Check if user is typing @mention
    const lastAt = val.lastIndexOf('@');
    if (lastAt >= 0 && lastAt === val.length - 1) {
      setShowSuggestions(true);
      setFilter('');
    } else if (lastAt >= 0) {
      const afterAt = val.substring(lastAt + 1);
      if (!afterAt.includes(' ')) {
        setShowSuggestions(true);
        setFilter(afterAt.toLowerCase());
      } else {
        setShowSuggestions(false);
      }
    } else {
      setShowSuggestions(false);
    }
  };

  const insertMention = (user) => {
    const lastAt = value.lastIndexOf('@');
    const before = value.substring(0, lastAt);
    onChange(`${before}@${user.email} `);
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  const filtered = users.filter(u =>
    u.name?.toLowerCase().includes(filter) || u.email?.toLowerCase().includes(filter)
  );

  return (
    <div className="relative">
      <div className="flex gap-2">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder || 'Skriv en kommentar... (bruk @ for å nevne noen)'}
          className="flex-1 bg-slate-800 border border-slate-700 rounded-xl px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
          data-testid="comment-input"
        />
        <button
          onClick={onSubmit}
          disabled={!value.trim()}
          className="px-4 py-2.5 bg-cyan-600 text-white rounded-xl hover:bg-cyan-500 disabled:opacity-40 transition-all"
          data-testid="comment-submit"
        >
          <Send className="w-4 h-4" />
        </button>
      </div>

      {showSuggestions && filtered.length > 0 && (
        <div className="absolute bottom-full left-0 mb-1 w-72 bg-slate-800 border border-slate-700 rounded-xl shadow-xl z-50 max-h-48 overflow-y-auto" data-testid="mention-suggestions">
          {filtered.slice(0, 8).map(user => (
            <button
              key={user.id}
              onClick={() => insertMention(user)}
              className="w-full px-3 py-2 text-left hover:bg-slate-700 flex items-center gap-2 text-sm"
              data-testid={`mention-user-${user.id}`}
            >
              <AtSign className="w-3 h-3 text-cyan-500 flex-shrink-0" />
              <div>
                <span className="text-white">{user.name}</span>
                <span className="text-slate-500 ml-2 text-xs">{user.email}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CommentSection({ entityType, entityId }) {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [resolveId, setResolveId] = useState(null);
  const [resolveNote, setResolveNote] = useState('');

  const token = localStorage.getItem('systemklart_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchComments = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/v2/comments/${entityType}/${entityId}`, { headers });
      setComments(res.data.comments);
    } catch (e) {
      console.error('Failed to load comments:', e);
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/v2/comments/users/mentionable`, { headers });
      setUsers(res.data.users);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchComments();
    fetchUsers();
  }, [fetchComments, fetchUsers]);

  const submitComment = async () => {
    if (!newComment.trim()) return;
    try {
      await axios.post(`${API}/api/v2/comments/${entityType}/${entityId}`, { text: newComment }, { headers });
      setNewComment('');
      fetchComments();
    } catch (e) {
      toast.error('Kunne ikke legge til kommentar');
    }
  };

  const resolveComment = async (commentId, status) => {
    try {
      await axios.patch(`${API}/api/v2/comments/${commentId}/resolve`, {
        status, note: resolveNote || null
      }, { headers });
      setResolveId(null);
      setResolveNote('');
      fetchComments();
      toast.success(status === 'accepted' ? 'Tatt til følge' : 'Markert som ikke aktuelt');
    } catch (e) {
      toast.error('Kunne ikke oppdatere kommentar');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return `${d.toLocaleDateString('nb-NO')} ${d.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' })}`;
  };

  // Highlight @mentions in text
  const renderText = (text) => {
    return text.split(/(@\S+)/g).map((part, i) => {
      if (part.startsWith('@')) {
        return <span key={i} className="text-cyan-400 font-medium">{part}</span>;
      }
      return part;
    });
  };

  const newCount = comments.filter(c => c.status === 'new').length;

  return (
    <div className="mt-6" data-testid="comment-section">
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-sm font-medium text-slate-300 hover:text-white transition-colors mb-3"
        data-testid="comment-toggle"
      >
        <MessageSquare className="w-4 h-4" />
        <span>Kommentarer ({comments.length})</span>
        {newCount > 0 && (
          <span className="px-1.5 py-0.5 text-xs bg-cyan-500/20 text-cyan-400 rounded-full">{newCount} nye</span>
        )}
        {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>

      {expanded && (
        <div className="space-y-3">
          {/* Comment list */}
          {comments.length === 0 && !loading && (
            <p className="text-xs text-slate-500 py-2">Ingen kommentarer ennå.</p>
          )}

          {comments.map(comment => (
            <div
              key={comment.id}
              className={`rounded-xl border p-3 transition-all ${
                comment.status === 'new'
                  ? 'bg-slate-800/60 border-slate-700/50'
                  : comment.status === 'accepted'
                  ? 'bg-emerald-950/20 border-emerald-800/30'
                  : 'bg-slate-800/30 border-slate-800/30 opacity-70'
              }`}
              data-testid={`comment-${comment.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-medium text-white">{comment.author_name}</span>
                    <span className="text-xs text-slate-500">{formatDate(comment.created_at)}</span>
                    <span className={`px-1.5 py-0.5 text-xs rounded-full ${STATUS_CONFIG[comment.status]?.color}`}>
                      {STATUS_CONFIG[comment.status]?.label}
                    </span>
                  </div>
                  <p className="text-sm text-slate-300 leading-relaxed">{renderText(comment.text)}</p>

                  {/* Resolution info */}
                  {comment.status !== 'new' && comment.resolved_by && (
                    <div className="mt-2 pt-2 border-t border-slate-700/30 text-xs text-slate-500">
                      {comment.status === 'accepted' ? 'Tatt til følge' : 'Ikke aktuelt'} av {comment.resolved_by} — {formatDate(comment.resolved_at)}
                      {comment.resolution_note && (
                        <p className="text-slate-400 mt-0.5">{comment.resolution_note}</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Resolve actions */}
                {comment.status === 'new' && (
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {resolveId === comment.id ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={resolveNote}
                          onChange={e => setResolveNote(e.target.value)}
                          placeholder="Notat (valgfritt)"
                          className="w-32 bg-slate-700 border border-slate-600 rounded-lg px-2 py-1 text-xs text-white"
                          data-testid="resolve-note-input"
                        />
                        <button
                          onClick={() => resolveComment(comment.id, 'accepted')}
                          className="p-1.5 bg-emerald-600/20 text-emerald-400 rounded-lg hover:bg-emerald-600/30"
                          title="Tatt til følge"
                          data-testid={`resolve-accept-${comment.id}`}
                        >
                          <Check className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => resolveComment(comment.id, 'dismissed')}
                          className="p-1.5 bg-slate-700/50 text-slate-400 rounded-lg hover:bg-slate-700"
                          title="Ikke aktuelt"
                          data-testid={`resolve-dismiss-${comment.id}`}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => { setResolveId(null); setResolveNote(''); }}
                          className="text-xs text-slate-500 hover:text-white"
                        >
                          Avbryt
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setResolveId(comment.id)}
                        className="px-2 py-1 text-xs text-slate-400 hover:text-white bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-all"
                        data-testid={`resolve-btn-${comment.id}`}
                      >
                        Svar ut
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* New comment input */}
          <MentionInput
            value={newComment}
            onChange={setNewComment}
            onSubmit={submitComment}
            users={users}
          />
        </div>
      )}
    </div>
  );
}
